import { useQueries } from '@tanstack/react-query';
import { canDo, type AccessContext, type Capability, type HubPlanetDto, type MyGrantDto } from '@aiai/shared';
import { fetchMyGrants } from '../api';

type PlanetRef = Pick<HubPlanetDto, 'id' | 'workspace'>;

/**
 * May the caller do `cap` on this planet, given their grants in the planet's
 * OWN workspace? Owners hold everything; a member holds only what a grant on
 * this project gives them. Grants not loaded yet means no (fail closed).
 */
export function planetCan(planet: PlanetRef, grants: MyGrantDto[] | undefined, cap: Capability): boolean {
  if (planet.workspace.role === 'owner') return true;
  if (!grants) return false;
  const ctx: AccessContext = { orgRole: 'member', grants };
  return canDo(ctx, cap, { projectId: planet.id });
}

const grantsKey = (slug: string) => ['myGrants', slug] as const;

/**
 * Planets (they may span workspaces): one grants fetch per member workspace,
 * shared by every caller through the same query key. Owners fetch nothing.
 */
export function usePlanetsCan(planets: readonly PlanetRef[]): (planet: PlanetRef, cap: Capability) => boolean {
  return usePlanetsCanState(planets).can;
}

/** Same as usePlanetsCan, plus whether any member workspace's grants are still loading. */
export function usePlanetsCanState(planets: readonly PlanetRef[]): {
  can: (planet: PlanetRef, cap: Capability) => boolean;
  loading: boolean;
} {
  const memberSlugs = [...new Set(planets.filter((p) => p.workspace.role !== 'owner').map((p) => p.workspace.slug))];
  const results = useQueries({
    queries: memberSlugs.map((slug) => ({
      queryKey: grantsKey(slug),
      queryFn: () => fetchMyGrants(slug),
    })),
  });
  const bySlug = new Map<string, MyGrantDto[] | undefined>(memberSlugs.map((s, i) => [s, results[i]?.data]));
  return {
    can: (planet, cap) => planetCan(planet, bySlug.get(planet.workspace.slug), cap),
    loading: results.some((r) => r.isLoading),
  };
}
